/* eslint-disable react/style-prop-object */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';

import {
  Sheet,
  DialogNew,
  Fieldset,
  Button,
  Poptip,
  Spinner,
  Table,
  Icon,
  Input,
  TabNav,
} from '@optimizely/axiom';

import { fetchUsers } from '../../api/userApi';
import EditPermission from './EditPermission';
import { roleDisplayNames, getUserName, searchFunction } from '../utils';

const Permission = (props) => {
  const { 
    accountId,
    projectId, 
    entityType, 
    entityId,
    entityKey,
    closeDialog, 
    enableTeams,
  } = props;

  const [userData, setUserData] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [activeTab, setActiveTab] = useState('user');

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchResults(null);
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    const dataToSearch = activeTab === 'user' ? userData : teamData;
    searchFunction(dataToSearch, query)
      .then((results) => setSearchResults(results))
      .catch((err) => console.error(err));
  };

  const closeModal = () => {
    setIsModalOpen(false);
    closeDialog();
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchUsers(projectId, entityType, entityId);
        setUserData(response.users);
        setTeamData(response.teams);
      } catch (err) {
        setError(
          'An error occurred while fetching users.',
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, entityId]);

  const isEditDisabled = (item) => {
    return item.role_inherited === 'admin' && item.role === 'admin';
  };

  return (
    <>
      {isModalOpen && (
        <Sheet
          testSection="permission-modal"
          title="Manage Permissions"
          subtitle="You can add or remove users or teams to manage who can access this item."
          hasCloseButton={true}
          hasRequiredFieldsIndicator={false}
          onClose={closeModal}
          footerButtonList={[
            <Button key={1} onClick={closeModal} style="highlight">
              Close
            </Button>,
          ]}>
          {enableTeams && (
            <TabNav activeTab={activeTab} style={['dashboard']}>
              <TabNav.Tab
                data-test-section="users-tab"
                onClick={() => handleTabChange('user')}
                tabId="user">
                Users
              </TabNav.Tab>
              <TabNav.Tab
                data-test-section="teams-tab"
                onClick={() => handleTabChange('team')}
                tabId="team">
                Teams
              </TabNav.Tab>
            </TabNav>
          )}

          <Fieldset title={activeTab === 'user' ? 'User' : 'Team'}>
            <Input
              isFilter={true}
              placeholder={activeTab === 'user' ? 'Search for a user' : 'Search for a team'}
              testSection={
                activeTab === 'user'
                  ? 'filter-permission-users'
                  : 'filter-permission-teams'
              }
              onChange={handleSearch}
            />
          </Fieldset>

          <Fieldset
            title={activeTab === 'user' ? 'Project Users' : null}
            titleSize="small">
            <Table density={'loose'} tableLayoutAlgorithm="auto">
              <Table.THead>
                <Table.TR>
                  {activeTab === 'user' ? (
                    <>
                      <Table.TH>User</Table.TH>
                    </>
                  ) : (
                    <>
                      <Table.TH>Team</Table.TH>
                    </>
                  )}
                  <Table.TH>
                    <span data-testid="table-role-header">
                      {entityType === 'environment'
                        ? 'Environment Role'
                        : entityType === 'flag'
                        ? 'Flag Role'
                        : entityType === 'audience'
                        ? 'Audience Role'
                        : ''}
                    </span>
                  </Table.TH>
                  <Table.TH></Table.TH>
                </Table.TR>
              </Table.THead>
              <Table.TBody>
                {!isLoading && (searchResults || (activeTab === 'user' ? userData : teamData)) && (
                  (searchResults || (activeTab === 'user' ? userData : teamData)).map((item, index) => {
                    return (
                      <Table.TR key={index}>
                        <Table.TD>
                          {activeTab === 'user' ? (
                            <>
                              <div>{getUserName(item)}</div>
                              <div className="muted" data-testid={`user-email-${item.id}`}>{item.email}</div>
                            </>
                          ) : (
                            <div>{item.name}</div>
                          )}
                        </Table.TD>
                        <Table.TD>
                          {(() => {
                            const disallowedTypes = ['flag', 'audience'];
                            const shouldDowngrade = item.role === 'publish' && disallowedTypes.includes(entityType);

                            if (shouldDowngrade) {
                              console.error(`'publish' role not allowed for ${entityType}s. Downgrading to 'edit' for ${entityType} ID: ${item.user_id}`);
                              item.role = 'edit';
                            }

                            const displayName = entityType === 'audience' && item.role === 'unknown'
                              ? <i>Check Details</i>
                              : roleDisplayNames[item.role] || '-';

                            return displayName === 'None' && entityType !== 'flag' ? 'Viewer' : displayName;
                          })()}
                        </Table.TD>
                        <Table.TD>
                          <Poptip
                            content="Role changes are disabled for Administrators and Project Owners"
                            disabled={!isEditDisabled(item)} // Only show tooltip when button is disabled
                            placement="top"
                          >
                            <Button 
                              className={`soft-half width--50 ${isEditDisabled(item) ? 'disabled' : ''}`}
                              size="large" 
                              data-testid={`edit-permission-button-${activeTab === 'user' ? item.id : item.id}`}
                              disabled={isEditDisabled(item)}
                              onClick={() => {
                                if (activeTab === 'user') {
                                  setSelectedUser(item);
                                } else {
                                  setSelectedTeam(item);
                                }
                              }}
                            >
                              <Icon 
                                color={isEditDisabled(item) ? "color--muted" : "color--base"} 
                                name="list-check" 
                                size="medium" 
                                background="transparent" 
                                transform="center" 
                              />
                            </Button>
                          </Poptip>
                        </Table.TD>
                      </Table.TR>
                    );
                  })
                )}
                {isLoading && (
                  <Table.TR>
                    <Table.TD colSpan={3}>
                      <div data-testid="loading-spinner">
                        <Spinner hasOverlay={true} />
                      </div>
                    </Table.TD>
                  </Table.TR>
                )}
              </Table.TBody>
            </Table>
          </Fieldset>
          {(selectedUser || selectedTeam) && (
            <EditPermission
              accountId={accountId}
              projectId={projectId}
              entityType={entityType}
              entityId={entityId}
              entityKey={entityKey}
              user={selectedUser}
              team={selectedTeam}
              setSelectedUser={setSelectedUser}
              setSelectedTeam={setSelectedTeam}
            />
          )}
        </Sheet>
      )}
      {error && (
        <DialogNew
          title="Loading issue"
          testSection="error-modal-permission"
          onClose={() => {
            setError(null);
            closeModal();
          }}
          footerButtonList={[
            <Button
              key="close"
              className="oui-button oui-button--plain oui-button--default"
              onClick={() => {
                setError(null);
                closeModal();
              }}>
              Close
            </Button>,
          ]}>
          <p>Unable to retrieve users permissions.</p>
        </DialogNew>
      )}
    </>
  );
};

Permission.propTypes = {};

export default Permission;
